import React, { useState, useEffect } from 'react';
import api from '../api/index.js';
import { API_BASE_URL } from '../config.js';
import './CoachSessionAssignments.css';

const STATION_LABEL = { vault: 'Vault', bars: 'Bars', beam: 'Beam', floor: 'Floor', trampoline: 'Tramp', tumble: 'Tumble' };
const norm = (s) => String(s || '').trim().toLowerCase();
const parseScope = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : null; } catch { return null; } }
  return null;
};
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

// A coach's "Today": read-only slices of session plans pushed to them, scoped to
// the events + groups they're responsible for.
function CoachSessionAssignments() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get(`${API_BASE_URL}/api/v1/practice-plans/my-assignments`)
      .then(r => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || items.length === 0) return null;

  return (
    <div className="csa">
      <h2 className="csa-title">📤 Your session plans today</h2>
      {items.map(a => {
        const plan = a.plan || {};
        const appSet = parseScope(a.apparatus_scope);
        const grpSet = parseScope(a.group_scope);
        const appAll = !appSet || !appSet.length;
        const grpAll = !grpSet || !grpSet.length;

        const groupById = Object.fromEntries((plan.groups || []).map(g => [g.id, g]));
        const inScope = (gp) =>
          (appAll || appSet.includes(norm(gp.apparatus))) && (grpAll || grpSet.includes(gp.group_id));

        // Timeline: whole-group blocks (context) + this coach's in-scope station cells
        const rows = [];
        (plan.slots || []).forEach((slot, i) => {
          const time = slot.start_time ? `${hhmm(slot.start_time)}${slot.end_time ? `–${hhmm(slot.end_time)}` : ''}` : '';
          const label = slot.label || `R${i + 1}`;
          if (slot.activity_label) {
            rows.push({ key: `w${slot.id}`, label, time, station: 'All', detail: slot.activity_label, whole: true });
          } else {
            (plan.group_placements || []).filter(gp => gp.slot_id === slot.id && inScope(gp)).forEach(gp => {
              const g = groupById[gp.group_id];
              rows.push({
                key: `${slot.id}-${gp.group_id}`, label, time,
                station: STATION_LABEL[norm(gp.apparatus)] || gp.apparatus,
                group: g?.group_label, headcount: g?.headcount,
                coach: gp.coach_name, note: gp.note
              });
            });
          }
        });

        // Drills for the events in scope, grouped by section
        const acts = (plan.activities || []).filter(a2 => appAll || appSet.includes(norm(a2.apparatus)));
        const sections = [];
        acts.forEach(a2 => {
          const s = a2.section || 'Drills';
          let bucket = sections.find(x => x.name === s);
          if (!bucket) { bucket = { name: s, items: [] }; sections.push(bucket); }
          bucket.items.push(a2);
        });

        const scopeLabel = `${appAll ? 'All events' : appSet.map(k => STATION_LABEL[k] || k).join(', ')} · ${grpAll ? 'All groups' : grpSet.map(id => groupById[id]?.group_label || `#${id}`).join(', ')}`;

        return (
          <div key={a.id} className="csa-card">
            <div className="csa-card-head">
              <div className="csa-card-title">{plan.class_name || a.class_name || 'Practice'}</div>
              <span className="csa-scope">{scopeLabel}</span>
            </div>
            {plan.theme && <div className="csa-theme">🎯 {plan.theme}</div>}
            {a.note && <div className="csa-note">📝 {a.note}</div>}
            {plan.goals && <div className="csa-goals">✅ {plan.goals}</div>}

            {rows.length > 0 && (
              <table className="csa-table">
                <tbody>
                  {rows.map(r => (
                    <tr key={r.key} className={r.whole ? 'csa-whole' : ''}>
                      <td className="csa-time">{r.label}{r.time ? ` · ${r.time}` : ''}</td>
                      <td className="csa-station">{r.station}</td>
                      <td className="csa-detail">
                        {r.whole ? r.detail : (
                          <>
                            <strong>{r.group}</strong>{r.headcount ? ` (${r.headcount})` : ''}
                            {r.coach && <span className="csa-coach"> · 🧑 {r.coach}</span>}
                            {r.note && <span className="csa-cellnote"> · {r.note}</span>}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {sections.map(sec => (
              <div key={sec.name} className="csa-sec">
                <div className="csa-sec-name">{sec.name}</div>
                <ul className="csa-sec-list">
                  {sec.items.map(it => (
                    <li key={it.id}>
                      {it.activity_name}
                      {it.reps != null && <span className="csa-count"> — {it.reps} {it.count_unit || ''}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

export default CoachSessionAssignments;
